import { Controller } from "@hotwired/stimulus"

// Server-rendered attribute errors (see ui/_form_errors) are distributed to
// their own fields: red border, message below the field, then scroll/focus
// the first invalid field. Fields keep their existing markup.
export default class extends Controller {
  connect() {
    this.form = this.element.closest("form")
    this.topBox = this.form?.querySelector('[role="alert"]')
    this.#reconcile()
    this.#applyErrors()
    // Defer until after every controller has connected: multi-step wizards
    // re-render their step visibility on connect and would otherwise undo the
    // reveal of the step holding the first invalid field.
    requestAnimationFrame(() => {
      this.#revealStepWithFirstInvalid()
      this.#focusFirstInvalid()
    })
  }

  #messages() {
    return Array.from(this.element.querySelectorAll("[data-form-error-message]"))
  }

  #applyErrors() {
    this.#messages().forEach((message) => this.#applyError(message))
  }

  #applyError(message) {
    const inputs = this.#findFields(message.dataset.formErrorField)

    if (!inputs || inputs.length === 0) {
      this.#surfaceInTopBox(message)
      return
    }

    inputs.forEach((input) => {
      input._formErrorMessage = message
      this.#markInvalid(input)
    })
    this.#messageHolder(inputs).insertAdjacentElement("afterend", message)
  }

  // Clear state from a previous submission. The error region is streamed in
  // while the form fields persist, so previously inserted messages and marks
  // would otherwise accumulate on every repeated failed submit.
  #reconcile() {
    this.form?.querySelectorAll("[data-form-error-message]").forEach((message) => {
      if (!this.element.contains(message)) message.remove()
    })
    this.form?.querySelectorAll("[data-form-error]").forEach((input) => this.#resetFieldMarkers(input))
  }

  #findFields(targetName) {
    if (!this.form) return null

    let fields = this.form.querySelectorAll(`[name="${targetName}"]`)
    if (fields.length === 0) {
      const attr = targetName.slice(targetName.lastIndexOf("[") + 1, -1)
      const base = targetName.slice(0, targetName.lastIndexOf("["))
      const candidates = [
        `${attr}_id`,
        `${attr}_ids`,
        `${attr.replace(/s$/, "")}_ids`,
      ]

      for (const candidate of candidates) {
        fields = this.form.querySelectorAll(`[name="${base}[${candidate}]"], [name="${base}[${candidate}][]"]`)
        if (fields.length > 0) break
      }
    }

    if (fields.length === 0) return null

    const matches = Array.from(fields)
    const visible = matches.filter((field) => field.type !== "hidden")

    // Hidden inputs can be companions of the real field (e.g. the empty
    // `country_ids[]` tag before a multiselect), skip them when a visible
    // match exists so anchoring and marking target the actual control.
    return visible.length > 0 ? visible : matches
  }

  #markInvalid(input) {
    if (input.type === "hidden") {
      const wrapper = input.closest("div")
      const visible = wrapper?.querySelector("input:not([type=hidden]), select, textarea")
      if (visible && visible !== input) {
        visible._formErrorMessage = input._formErrorMessage
        delete input._formErrorMessage
        input = visible
      }
    }

    input.dataset.formError = "true"
    input.classList.add("border-red-500", "focus:ring-red-500")
    input.setAttribute("aria-invalid", "true")

    if (input.type === "radio" || input.type === "checkbox") {
      input.classList.add("accent-error")
    }

    const tomSelect = input.closest(".ts-wrapper")?.querySelector(".ts-control") ||
      (input.tomselect || input.classList.contains("tomselected") ? input.parentElement?.querySelector(".ts-control") : null)
    if (tomSelect) {
      tomSelect.classList.add("border-red-500", "ring-2", "ring-red-200")
    }

    if (!input.dataset.formErrorListener) {
      input.dataset.formErrorListener = "true"
      input.addEventListener("input", () => this.#clearError(input))
      input.addEventListener("change", () => this.#clearError(input))
    }
  }

  #messageHolder(inputs) {
    const input = inputs[0]

    if (input.closest(".ts-wrapper")) return input.closest(".ts-wrapper")

    if (input.type === "hidden") {
      const wrapper = input.closest("div")
      const visible = wrapper?.querySelector("input:not([type=hidden]), select, textarea")
      return visible || input
    }

    if (input.type === "radio" || input.type === "checkbox") {
      return input.closest("div") || input
    }

    return input
  }

  #surfaceInTopBox(message) {
    const wrapper = this.topBox?.querySelector(".mt-2")
    if (!wrapper) return

    const element = document.createElement(wrapper.querySelector("ul") ? "li" : "p")
    element.textContent = message.textContent
    wrapper.appendChild(element)
  }

  #clearError(input) {
    if (!input.dataset.formError) return

    this.#resetFieldMarkers(input)
    input._formErrorMessage?.remove()
    delete input._formErrorMessage
  }

  #resetFieldMarkers(input) {
    delete input.dataset.formError
    input.classList.remove("border-red-500", "focus:ring-red-500", "accent-error")
    input.removeAttribute("aria-invalid")
    input.closest(".ts-wrapper")?.querySelector(".ts-control")?.classList.remove("border-red-500", "ring-2", "ring-red-200")
    if (input.tomselect || input.classList.contains("tomselected")) {
      input.parentElement?.querySelector(".ts-control")?.classList.remove("border-red-500", "ring-2", "ring-red-200")
    }
  }

  #firstInvalidInput() {
    for (const message of this.#allMessages()) {
      const inputs = this.#findFields(message.dataset.formErrorField)
      if (inputs?.length) return inputs[0]
    }
    return null
  }

  #allMessages() {
    return this.form ? Array.from(this.form.querySelectorAll("[data-form-error-message]")) : this.#messages()
  }

  #revealStepWithFirstInvalid() {
    const first = this.#firstInvalidInput()
    if (!first) return

    const step = first.closest('[data-multi-step-form-target="step"]')
    if (!step) return

    const steps = Array.from(this.form.querySelectorAll('[data-multi-step-form-target="step"]'))
    steps.forEach((element) => element.classList.toggle("hidden", element !== step))
  }

  #focusTarget(input) {
    if (input.closest(".ts-wrapper")) {
      return input.closest(".ts-wrapper")
    }
    if (input.tomselect || input.classList.contains("tomselected")) {
      const control = input.parentElement?.querySelector(".ts-control")
      if (control) return control
    }
    if (input.type === "hidden") {
      const wrapper = input.closest("div")
      return wrapper?.querySelector("input:not([type=hidden]), select, textarea") || input
    }
    return input
  }

  #focusFirstInvalid() {
    const first = this.#firstInvalidInput()
    if (!first) return

    const isTomSelectField = first.matches?.("[data-controller='tom-select']")
    let scrolledTarget = null

    requestAnimationFrame(() => {
      const target = this.#focusTarget(first)
      if (!target) return
      scrolledTarget = target
      target.scrollIntoView({ behavior: "smooth", block: "center" })
    })

    const focusControl = (attempt = 0) => {
      if (attempt > 25) return

      // Re-resolve the target: on a re-rendered form the tom-select may still
      // be hydrating, in which case #focusTarget returns the raw hidden select.
      const target = this.#focusTarget(first)
      if (!target) return

      if (target !== scrolledTarget) {
        scrolledTarget = target
        target.scrollIntoView({ behavior: "smooth", block: "center" })
      }

      const focusable = target.querySelector?.("input") || target
      focusable.focus?.({ preventScroll: true })

      const hydrated = !!(first.tomselect || first.classList.contains("tomselected"))
      const landed = focusable === document.activeElement || focusable.contains?.(document.activeElement)
      if ((!landed || (isTomSelectField && !hydrated)) && attempt < 25) {
        window.setTimeout(() => focusControl(attempt + 1), 100)
      }
    }

    window.setTimeout(() => focusControl(), 150)
  }
}